import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditCorrectiveAction, AuditEvent, AuditEvidence, AuditFinding, AuditFindingLink, AuditTransitionRequest } from '../audit.models';
import { AuditService } from '../audit.service';
import {
  actionPriorityOptions,
  actionTypeOptions,
  auditNavItems,
  escalationLevelOptions,
  escalationStatusOptions,
  evidenceTypeOptions,
  findingTypeOptions,
  findingStatusOptions,
  formatAuditDate,
  impactScopeOptions,
  severityOptions,
  statusSeverity
} from '../audit-ui';

@Component({
  selector: 'app-audit-finding-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Finding" [title]="finding.findingNumber || 'Audit Finding'" [subtitle]="finding.title || 'Nonconformity, observation, or risk record.'">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" severity="secondary" [outlined]="true"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/findings" icon="pi pi-arrow-left" label="Findings" severity="secondary" [outlined]="true"></a>
          <p-button label="Save" icon="pi pi-save" (onClick)="saveFinding()" [loading]="saving" [disabled]="!canManage"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <section class="detail-grid">
        <app-page-data-panel title="Finding Details" subtitle="Ownership, due date, root cause, and impact." [resultText]="finding.status || '-'">
          <div class="audit-form-grid">
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="finding-severity" [options]="severityOptions" [(ngModel)]="finding.severity" optionLabel="label" optionValue="value" [disabled]="!canManage"></p-select>
                <label for="finding-severity">Severity</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-info-circle"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="finding-status" [options]="findingStatusOptions" [(ngModel)]="finding.status" optionLabel="label" optionValue="value" [disabled]="true"></p-select>
                <label for="finding-status">Status</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-tags"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="finding-type" [options]="findingTypeOptions" [(ngModel)]="finding.findingType" optionLabel="label" optionValue="value" [disabled]="!canManage"></p-select>
                <label for="finding-type">Finding Type</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="finding-risk-rating" [options]="severityOptions" [(ngModel)]="finding.riskRating" optionLabel="label" optionValue="value" [disabled]="!canManage"></p-select>
                <label for="finding-risk-rating">Risk Rating</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-inputNumber inputId="finding-owner-user" [(ngModel)]="finding.ownerUserId" [useGrouping]="false" [disabled]="!canManage"></p-inputNumber>
                <label for="finding-owner-user">Owner User ID</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-inputNumber inputId="finding-owner-company" [(ngModel)]="finding.ownerCompanyId" [useGrouping]="false" [disabled]="!canManage"></p-inputNumber>
                <label for="finding-owner-company">Owner Company ID</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-calendar-clock"></i></p-inputgroup-addon>
              <p-iftalabel>
                <input pInputText id="finding-due-at" type="datetime-local" [(ngModel)]="dueAtInput" [disabled]="!canManage">
                <label for="finding-due-at">Due Date</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-stopwatch"></i></p-inputgroup-addon>
              <p-iftalabel>
                <input pInputText id="finding-sla-due-at" type="datetime-local" [(ngModel)]="slaDueAtInput" [disabled]="!canManage">
                <label for="finding-sla-due-at">SLA Due</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-compass"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="finding-impact-scope" [options]="impactScopeOptions" [(ngModel)]="finding.impactScope" optionLabel="label" optionValue="value" [showClear]="true" [disabled]="!canManage"></p-select>
                <label for="finding-impact-scope">Impact Scope</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-inputNumber inputId="finding-financial-impact" [(ngModel)]="finding.financialImpactAmount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" [disabled]="!canManage"></p-inputNumber>
                <label for="finding-financial-impact">Financial Impact</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-angle-double-up"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="finding-escalation-level" [options]="escalationLevelOptions" [(ngModel)]="finding.escalationLevel" optionLabel="label" optionValue="value" [disabled]="!canManage"></p-select>
                <label for="finding-escalation-level">Escalation Level</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-bell"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="finding-escalation-status" [options]="escalationStatusOptions" [(ngModel)]="finding.escalationStatus" optionLabel="label" optionValue="value" [disabled]="!canManage"></p-select>
                <label for="finding-escalation-status">Escalation Status</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-heading"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="finding-title" rows="2" [(ngModel)]="finding.title" [disabled]="!canManage"></textarea>
                <label for="finding-title">Title</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="finding-description" rows="4" [(ngModel)]="finding.description" [disabled]="!canManage"></textarea>
                <label for="finding-description">Description</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="finding-root-cause" rows="3" [(ngModel)]="finding.rootCause" [disabled]="!canManage"></textarea>
                <label for="finding-root-cause">Root Cause</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-chart-line"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="finding-operational-impact" rows="3" [(ngModel)]="finding.operationalImpact" [disabled]="!canManage"></textarea>
                <label for="finding-operational-impact">Operational Impact</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-users"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="finding-customer-impact" rows="3" [(ngModel)]="finding.customerImpact" [disabled]="!canManage"></textarea>
                <label for="finding-customer-impact">Customer Impact</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="finding-carrier-impact" rows="3" [(ngModel)]="finding.carrierImpact" [disabled]="!canManage"></textarea>
                <label for="finding-carrier-impact">Carrier Impact</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-bell"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="finding-escalation-notes" rows="3" [(ngModel)]="finding.escalationNotes" [disabled]="!canManage"></textarea>
                <label for="finding-escalation-notes">Escalation Notes</label>
              </p-iftalabel>
            </p-inputgroup>
          </div>
        </app-page-data-panel>

        <app-page-data-panel title="Controls" subtitle="Finding transitions and source case." [resultText]="finding.findingType || '-'">
          <div class="context-stack">
            <p-tag [value]="finding.status || '-'" [severity]="statusSeverity(finding.status)"></p-tag>
            <p-tag [value]="finding.severity || '-'" [severity]="statusSeverity(finding.severity)"></p-tag>
            @if (finding.repeatFinding) {
              <p-tag value="Repeat Finding" severity="warn"></p-tag>
            }
            @if (finding.slaBreached || finding.overdue) {
              <p-tag [value]="(finding.daysOverdue || 1) + ' days overdue'" severity="danger"></p-tag>
            }
            <a pButton [routerLink]="['/audit/cases', finding.auditCaseId]" label="Open Case" icon="pi pi-arrow-right" iconPos="right" [outlined]="true" severity="secondary"></a>
            <p-button label="Escalate" icon="pi pi-bell" severity="danger" [outlined]="true" (onClick)="openEscalate()" [disabled]="!canManage || finding.status === 'CLOSED'"></p-button>
            <p-button label="Close Finding" icon="pi pi-lock" severity="secondary" (onClick)="closeFinding()" [disabled]="!canManage || finding.status === 'CLOSED'"></p-button>
            <p-button label="Reopen Finding" icon="pi pi-refresh" severity="warn" [outlined]="true" (onClick)="openReopen()" [disabled]="!canManage || finding.status === 'OPEN'"></p-button>
          </div>
        </app-page-data-panel>
      </section>

      <section class="detail-grid">
        <app-page-data-panel title="Corrective Actions" subtitle="Actions linked to this finding." [resultText]="actions.length + ' actions'">
          <div class="panel-actions">
            <p-button label="New Action" icon="pi pi-plus" (onClick)="openAction()" [disabled]="!canManageActions"></p-button>
          </div>
          <p-table [value]="actions" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header">
              <tr><th>Action</th><th>Priority</th><th>Status</th><th>Due</th><th class="text-right">Open</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-action>
              <tr>
                <td>{{ action.actionNumber || '-' }}</td>
                <td><p-tag [value]="action.priority || '-'" [severity]="statusSeverity(action.priority)"></p-tag></td>
                <td><p-tag [value]="action.status || '-'" [severity]="statusSeverity(action.status)"></p-tag></td>
                <td>{{ formatDate(action.dueAt) }}</td>
                <td class="text-right"><a pButton [routerLink]="['/audit/actions', action.actionId]" icon="pi pi-arrow-right" [text]="true"></a></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5" class="text-center p-4">No corrective actions have been added.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Evidence" subtitle="Evidence metadata linked to this finding." [resultText]="evidence.length + ' records'">
          <div class="panel-actions">
            <p-button label="Add Evidence" icon="pi pi-plus" (onClick)="openEvidence()" [disabled]="!canUploadEvidence"></p-button>
          </div>
          <p-table [value]="evidence" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header">
              <tr><th>Evidence</th><th>Type</th><th>Captured</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.fileName || item.sourceDisplayName || '-' }}</td>
                <td>{{ item.evidenceType || '-' }}</td>
                <td>{{ formatDate(item.capturedAt) }}</td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      <app-page-data-panel title="Related Historical Findings" subtitle="Repeat finding detection and manually linked historical issues." [resultText]="links.length + ' links'">
        <div class="panel-actions">
          <p-button label="Link Finding" icon="pi pi-link" (onClick)="openLink()" [disabled]="!canManage"></p-button>
          <p-button label="Refresh Candidates" icon="pi pi-refresh" severity="secondary" [outlined]="true" (onClick)="loadRelated()" [disabled]="!finding.findingId"></p-button>
        </div>
        <p-table [value]="links" styleClass="p-datatable-sm" responsiveLayout="scroll">
          <ng-template pTemplate="header">
            <tr><th>Related Finding</th><th>Type</th><th>Severity</th><th>Status</th><th>Reason</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-link>
            <tr>
              <td>
                <a [routerLink]="['/audit/findings', link.relatedFindingId]">{{ link.relatedFindingNumber || ('Finding #' + link.relatedFindingId) }}</a>
                <div class="muted">{{ link.relatedFindingTitle || '-' }}</div>
              </td>
              <td>{{ link.linkType || '-' }}</td>
              <td><p-tag [value]="link.relatedFindingSeverity || '-'" [severity]="statusSeverity(link.relatedFindingSeverity)"></p-tag></td>
              <td><p-tag [value]="link.relatedFindingStatus || '-'" [severity]="statusSeverity(link.relatedFindingStatus)"></p-tag></td>
              <td>{{ link.reason || '-' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="5" class="text-center p-4">No related findings are linked.</td></tr>
          </ng-template>
        </p-table>
        @if (repeatCandidates.length) {
          <div class="candidate-strip">
            @for (candidate of repeatCandidates; track candidate.findingId) {
              <button type="button" class="candidate-chip" (click)="linkCandidate(candidate)">
                {{ candidate.findingNumber || ('Finding #' + candidate.findingId) }} · {{ candidate.severity || '-' }}
              </button>
            }
          </div>
        }
      </app-page-data-panel>

      <app-page-data-panel title="Timeline" subtitle="Audit case events related to this finding and actions." [resultText]="events.length + ' events'">
        <p-table [value]="events" styleClass="p-datatable-sm" responsiveLayout="scroll">
          <ng-template pTemplate="header">
            <tr><th>Event</th><th>Status</th><th>When</th><th>Comment</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-event>
            <tr>
              <td>{{ event.eventType }}</td>
              <td>{{ event.fromStatus || '-' }} -> {{ event.toStatus || '-' }}</td>
              <td>{{ formatDate(event.performedAt) }}</td>
              <td>{{ event.comment || '-' }}</td>
            </tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>

    <p-dialog header="New Corrective Action" [(visible)]="actionVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(760px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-wrench"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-action-type" [options]="actionTypeOptions" [(ngModel)]="actionForm.actionType" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-action-type">Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-action-priority" [options]="priorityOptions" [(ngModel)]="actionForm.priority" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-action-priority">Priority</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-inputNumber inputId="finding-action-owner" [(ngModel)]="actionForm.ownerUserId" [useGrouping]="false"></p-inputNumber>
            <label for="finding-action-owner">Owner User ID</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-calendar-clock"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="finding-action-due-at" type="datetime-local" [(ngModel)]="actionDueAtInput">
            <label for="finding-action-due-at">Due Date</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-action-description" rows="4" [(ngModel)]="actionForm.description"></textarea>
            <label for="finding-action-description">Description</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="actionVisible = false"></p-button>
        <p-button label="Create Action" icon="pi pi-check" (onClick)="saveAction()" [loading]="saving"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Reopen Finding" [(visible)]="reopenVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(600px, 96vw)' }">
      <p-inputgroup>
        <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
        <p-iftalabel>
          <textarea pTextarea id="finding-reopen-reason" rows="4" [(ngModel)]="transition.reason"></textarea>
          <label for="finding-reopen-reason">Reason</label>
        </p-iftalabel>
      </p-inputgroup>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="reopenVisible = false"></p-button>
        <p-button label="Reopen" icon="pi pi-refresh" (onClick)="reopenFinding()" [loading]="saving"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Escalate Finding" [(visible)]="escalateVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(640px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-angle-double-up"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-escalate-level" [options]="escalationLevelOptions" [(ngModel)]="finding.escalationLevel" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-escalate-level">Escalation Level</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-escalate-reason" rows="4" [(ngModel)]="transition.comment"></textarea>
            <label for="finding-escalate-reason">Escalation Note</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="escalateVisible = false"></p-button>
        <p-button label="Escalate" icon="pi pi-bell" severity="danger" (onClick)="escalateFinding()" [loading]="saving"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Link Related Finding" [(visible)]="linkVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(680px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-inputNumber inputId="related-finding-id" [(ngModel)]="linkForm.relatedFindingId" [useGrouping]="false"></p-inputNumber>
            <label for="related-finding-id">Related Finding ID</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="related-link-type" [options]="linkTypeOptions" [(ngModel)]="linkForm.linkType" optionLabel="label" optionValue="value"></p-select>
            <label for="related-link-type">Link Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="related-reason" rows="4" [(ngModel)]="linkForm.reason"></textarea>
            <label for="related-reason">Reason</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="linkVisible = false"></p-button>
        <p-button label="Link Finding" icon="pi pi-link" (onClick)="saveLink()" [loading]="saving" [disabled]="!canManage"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Add Finding Evidence" [(visible)]="evidenceVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(700px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-paperclip"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-evidence-type" [options]="evidenceTypeOptions" [(ngModel)]="evidenceForm.evidenceType" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-evidence-type">Evidence Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-evidence-url" rows="2" [(ngModel)]="evidenceForm.fileUrl"></textarea>
            <label for="finding-evidence-url">File URL</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-file"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-evidence-name" rows="2" [(ngModel)]="evidenceForm.fileName"></textarea>
            <label for="finding-evidence-name">File Name / Description</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="evidenceVisible = false"></p-button>
        <p-button label="Add Evidence" icon="pi pi-check" (onClick)="saveEvidence()" [loading]="saving" [disabled]="!canUploadEvidence"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .detail-grid {
      display: grid;
      grid-template-columns: minmax(0, 1.25fr) minmax(280px, .75fr);
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .audit-form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(180px, 1fr));
      gap: .85rem;
    }

    label,
    .context-stack {
      color: #334155;
      display: grid;
      gap: .45rem;
      min-width: 0;
    }

    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select,
    p-inputNumber {
      width: 100%;
    }

    .wide-field {
      grid-column: 1 / -1;
    }

    .panel-actions {
      display: flex;
      justify-content: flex-end;
      gap: .5rem;
      margin-bottom: 1rem;
    }

    .muted {
      color: #64748b;
      font-size: .82rem;
      margin-top: .2rem;
    }

    .candidate-strip {
      display: flex;
      flex-wrap: wrap;
      gap: .5rem;
      margin-top: 1rem;
    }

    .candidate-chip {
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 999px;
      color: #334155;
      cursor: pointer;
      font: inherit;
      font-weight: 700;
      padding: .35rem .7rem;
    }

    .candidate-chip:hover {
      background: #e0f2fe;
      border-color: #38bdf8;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #991b1b;
      font-weight: 700;
      margin-top: .85rem;
      padding: .75rem;
    }

    @media (max-width: 980px) {
      .detail-grid,
      .audit-form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditFindingDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly auditService = inject(AuditService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  actionVisible = false;
  reopenVisible = false;
  escalateVisible = false;
  linkVisible = false;
  evidenceVisible = false;
  dialogError = '';
  finding: AuditFinding = {};
  actions: AuditCorrectiveAction[] = [];
  evidence: AuditEvidence[] = [];
  events: AuditEvent[] = [];
  links: AuditFindingLink[] = [];
  repeatCandidates: AuditFinding[] = [];
  actionForm: AuditCorrectiveAction = {};
  evidenceForm: AuditEvidence = {};
  linkForm: AuditFindingLink = {};
  transition: AuditTransitionRequest = {};
  dueAtInput = '';
  slaDueAtInput = '';
  actionDueAtInput = '';

  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canManage = this.permissionService.hasPermission(app_permissions.audit.findings.manage);
  readonly canManageActions = this.permissionService.hasPermission(app_permissions.audit.actions.manage);
  readonly canUploadEvidence = this.permissionService.hasPermission(app_permissions.audit.evidence.upload);
  readonly severityOptions = severityOptions;
  readonly findingTypeOptions = findingTypeOptions;
  readonly impactScopeOptions = impactScopeOptions;
  readonly escalationLevelOptions = escalationLevelOptions;
  readonly escalationStatusOptions = escalationStatusOptions;
  readonly findingStatusOptions = findingStatusOptions;
  readonly actionTypeOptions = actionTypeOptions;
  readonly priorityOptions = actionPriorityOptions;
  readonly evidenceTypeOptions = evidenceTypeOptions;
  readonly linkTypeOptions = [
    { label: 'Related', value: 'RELATED' },
    { label: 'Repeat Of', value: 'REPEAT_OF' },
    { label: 'Same Root Cause', value: 'SAME_ROOT_CAUSE' },
    { label: 'Same Source', value: 'SAME_SOURCE' }
  ];
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Status', value: this.finding.status || '-' },
      { label: 'Severity', value: this.finding.severity || '-', tone: this.finding.severity === 'CRITICAL' ? 'danger' : 'warn' },
      { label: 'Actions', value: this.actions.length, tone: 'info' },
      { label: 'Evidence', value: this.evidence.length },
      { label: 'Related', value: this.links.length },
      { label: 'SLA', value: this.finding.slaBreached ? 'Breached' : 'Tracked', tone: this.finding.slaBreached ? 'danger' : 'success' }
    ];
  }

  load(): void {
    const findingId = Number(this.route.snapshot.paramMap.get('id'));
    this.loading = true;
    this.auditService.getFinding(findingId).pipe(
      switchMap(findingResponse => {
        this.finding = findingResponse.data ?? {};
        this.dueAtInput = this.toLocalInput(this.finding.dueAt);
        this.slaDueAtInput = this.toLocalInput(this.finding.slaDueAt);
        return forkJoin({
          actions: this.auditService.findingActions(findingId),
          evidence: this.auditService.evidence(undefined, undefined, findingId),
          links: this.auditService.findingLinks(findingId),
          repeatCandidates: this.auditService.repeatCandidates(findingId),
          events: this.finding.auditCaseId ? this.auditService.caseEvents(this.finding.auditCaseId) : of({ data: [] as AuditEvent[] })
        });
      })
    ).subscribe({
      next: response => {
        this.actions = response.actions.data ?? [];
        this.evidence = response.evidence.data ?? [];
        this.links = response.links.data ?? [];
        this.repeatCandidates = response.repeatCandidates.data ?? [];
        this.events = response.events.data ?? [];
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Finding', detail: 'Unable to load audit finding.' }),
      complete: () => this.loading = false
    });
  }

  saveFinding(): void {
    this.finding.dueAt = this.dueAtInput ? new Date(this.dueAtInput).toISOString() : undefined;
    this.finding.slaDueAt = this.slaDueAtInput ? new Date(this.slaDueAtInput).toISOString() : undefined;
    this.saving = true;
    this.auditService.updateFinding(this.finding).subscribe({
      next: response => {
        this.finding = response.data ?? this.finding;
        this.messageService.add({ severity: 'success', summary: 'Finding saved', detail: response.message || 'Finding saved.' });
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Finding', detail: error?.error?.message || 'Unable to save finding.' }),
      complete: () => this.saving = false
    });
  }

  openAction(): void {
    this.dialogError = '';
    this.actionForm = { actionType: 'CORRECTIVE', priority: this.finding.severity || 'MEDIUM' };
    this.actionDueAtInput = '';
    this.actionVisible = true;
  }

  saveAction(): void {
    this.dialogError = '';
    if (!this.actionForm.description?.trim()) {
      this.dialogError = 'Description is required.';
      return;
    }
    this.actionForm.dueAt = this.actionDueAtInput ? new Date(this.actionDueAtInput).toISOString() : undefined;
    this.saving = true;
    this.auditService.createFindingAction(this.finding.findingId!, this.actionForm).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Action created', detail: response.message || 'Corrective action created.' });
        this.actionVisible = false;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to create corrective action.',
      complete: () => this.saving = false
    });
  }

  closeFinding(): void {
    this.saving = true;
    this.auditService.closeFinding(this.finding.findingId!, { comment: 'Closed from finding detail.' }).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Finding closed', detail: response.message || 'Finding closed.' });
        this.finding = response.data ?? this.finding;
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Finding', detail: error?.error?.message || 'Unable to close finding.' }),
      complete: () => this.saving = false
    });
  }

  openReopen(): void {
    this.dialogError = '';
    this.transition = {};
    this.reopenVisible = true;
  }

  openEscalate(): void {
    this.dialogError = '';
    this.transition = { comment: this.finding.escalationNotes };
    this.escalateVisible = true;
  }

  escalateFinding(): void {
    this.dialogError = '';
    if (!this.transition.comment?.trim()) {
      this.dialogError = 'An escalation note is required.';
      return;
    }
    this.saving = true;
    this.auditService.updateFinding(this.finding).pipe(
      switchMap(() => this.auditService.escalateFinding(this.finding.findingId!, this.transition))
    ).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Finding escalated', detail: response.message || 'Finding escalated.' });
        this.escalateVisible = false;
        this.finding = response.data ?? this.finding;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to escalate finding.',
      complete: () => this.saving = false
    });
  }

  loadRelated(): void {
    if (!this.finding.findingId) {
      return;
    }
    forkJoin({
      links: this.auditService.findingLinks(this.finding.findingId),
      repeatCandidates: this.auditService.repeatCandidates(this.finding.findingId)
    }).subscribe({
      next: response => {
        this.links = response.links.data ?? [];
        this.repeatCandidates = response.repeatCandidates.data ?? [];
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Related findings', detail: 'Unable to load related findings.' })
    });
  }

  openLink(): void {
    this.dialogError = '';
    this.linkForm = { linkType: 'RELATED' };
    this.linkVisible = true;
  }

  linkCandidate(candidate: AuditFinding): void {
    this.linkForm = {
      relatedFindingId: candidate.findingId,
      linkType: 'REPEAT_OF',
      reason: candidate.findingNumber ? `Repeat of ${candidate.findingNumber}.` : 'Repeat finding candidate.'
    };
    this.saveLink();
  }

  saveLink(): void {
    this.dialogError = '';
    if (!this.linkForm.relatedFindingId) {
      this.dialogError = 'Related finding ID is required.';
      return;
    }
    this.saving = true;
    this.auditService.linkFinding(this.finding.findingId!, this.linkForm).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Finding linked', detail: response.message || 'Related finding linked.' });
        this.linkVisible = false;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to link related finding.',
      complete: () => this.saving = false
    });
  }

  reopenFinding(): void {
    this.dialogError = '';
    if (!this.transition.reason?.trim()) {
      this.dialogError = 'A reopen reason is required.';
      return;
    }
    this.saving = true;
    this.auditService.reopenFinding(this.finding.findingId!, this.transition).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Finding reopened', detail: response.message || 'Finding reopened.' });
        this.reopenVisible = false;
        this.finding = response.data ?? this.finding;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to reopen finding.',
      complete: () => this.saving = false
    });
  }

  openEvidence(): void {
    this.dialogError = '';
    this.evidenceForm = { evidenceType: 'DOCUMENT', findingId: this.finding.findingId, auditCaseId: this.finding.auditCaseId };
    this.evidenceVisible = true;
  }

  saveEvidence(): void {
    this.dialogError = '';
    if (!this.evidenceForm.evidenceType) {
      this.dialogError = 'Evidence type is required.';
      return;
    }
    this.saving = true;
    this.auditService.createEvidence(this.evidenceForm).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Evidence added', detail: response.message || 'Evidence added.' });
        this.evidenceVisible = false;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to add evidence.',
      complete: () => this.saving = false
    });
  }

  private toLocalInput(value?: string | Date): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }
}
